import React, { useMemo } from 'react';
import { Badge, Text } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import moment from 'moment';
import { DataTable } from '../../../../../../components/datatable';
import { ILivroFiscalNota } from '../../../../../../types/icms';
import { formatCNPJ, formatCurrency, toNumber } from '../../../../../../utils/FormatFunctions';
import { IPagination } from '../../../../../../types/shared';

interface LivrosTableProps {
  data: ILivroFiscalNota[];
  pagination: IPagination;
  isFetching?: boolean;
}

const classificacaoColor: Record<string, string> = {
  difal: 'orange',
  st: 'purple',
  simples: 'blue',
  isenta: 'gray',
  nao_tributada: 'gray',
  credito_outros: 'cyan',
  servico: 'teal',
  outras: 'gray',
};

export function LivrosTable({ data, pagination, isFetching }: LivrosTableProps) {
  const columns = useMemo<ColumnDef<ILivroFiscalNota>[]>(
    () => [
      {
        header: 'Emissão',
        accessorKey: 'dt_emissao',
        cell: info => {
          const v = info.getValue() as string;
          return v ? moment(v).format('DD/MM/YYYY') : '—';
        },
      },
      {
        header: 'Nº',
        accessorKey: 'num_nota',
      },
      {
        header: 'Série',
        accessorKey: 'serie',
        cell: info => (info.getValue() as string) || '—',
      },
      {
        header: 'Modelo',
        accessorKey: 'modelo',
        cell: info => (info.getValue() as string) || '—',
      },
      {
        header: 'Cliente / Fornecedor',
        accessorKey: 'nome_clifor',
        cell: info => {
          const row = info.row.original;
          return (
            <div className="text-left">
              <Text fontSize="12px" fontWeight="medium" noOfLines={1}>
                {row.nome_clifor || '—'}
              </Text>
              <Text fontSize="10px" color="gray.500">
                {row.cnpj_clifor ? formatCNPJ(row.cnpj_clifor) : (row.cgce_clifor ?? '')}
                {row.uf_clifor ? ` · ${row.uf_clifor}` : ''}
              </Text>
            </div>
          );
        },
      },
      {
        header: 'CFOP',
        accessorKey: 'cfop',
        cell: info => (info.getValue() as string) || '—',
      },
      {
        header: 'Acumulador',
        accessorKey: 'codi_acu',
        cell: info => {
          const row = info.row.original;
          if (!row.codi_acu) return '—';
          return (
            <div className="text-left">
              <Text fontSize="12px" fontWeight="medium">
                {row.codi_acu}
              </Text>
              {row.nome_acu && (
                <Text fontSize="10px" color="gray.500" noOfLines={1}>
                  {row.nome_acu}
                </Text>
              )}
              {row.classificacao_acumulador && (
                <Badge
                  colorScheme={classificacaoColor[row.classificacao_acumulador] ?? 'gray'}
                  fontSize="9px"
                  mt={1}
                >
                  {row.classificacao_acumulador}
                </Badge>
              )}
            </div>
          );
        },
      },
      {
        header: 'V. Contábil',
        accessorKey: 'vcontabil',
        cell: info => formatCurrency(toNumber(info.getValue() as number | string)),
      },
      {
        header: 'Base ICMS',
        accessorKey: 'base_icms',
        cell: info => formatCurrency(toNumber(info.getValue() as number | string)),
      },
      {
        header: 'ICMS',
        accessorKey: 'valor_icms',
        cell: info => formatCurrency(toNumber(info.getValue() as number | string)),
      },
    ],
    [],
  );

  return (
    <DataTable<ILivroFiscalNota>
      columns={columns}
      data={data}
      pagination={pagination}
      isFetching={isFetching}
      emptyMsg="Nenhuma nota encontrada para o período."
      loadingMsg="Carregando notas..."
    />
  );
}
